// NO-872: the RD Schedule action API under /api/rd-schedule. Request and
// response shapes only; every response carries ids, times and closed codes the
// dialogs need, never Healthie's text.

export const RD_SCHEDULE_REPEAT_INTERVALS = [
  "weekly",
  "biweekly",
  "every_4_weeks",
] as const;
export type RdScheduleRepeatInterval = (typeof RD_SCHEDULE_REPEAT_INTERVALS)[number];

export const RD_SCHEDULE_REPEAT_MIN = 2;
export const RD_SCHEDULE_REPEAT_MAX = 26;

export const RD_SCHEDULE_OUTCOMES = [
  "client_cancelled",
  "rd_cancelled",
  "late_cancel",
  "no_show",
] as const;
export type RdScheduleOutcome = (typeof RD_SCHEDULE_OUTCOMES)[number];

export type RdScheduleActionScope = "this" | "this_and_later";
export type RdScheduleFeeDecision = "charge" | "waive";
export type RdScheduleOccurrenceStatus =
  | "open"
  | "outside_hours"
  | "booked"
  | "blocked"
  | "past";

export interface RdScheduleSlot {
  startsAt: string;
  /** `HH:MM` in the RD's zone. */
  localTime: string;
}

export interface RdScheduleOpenTimesResponse {
  data: {
    date: string;
    timeZone: string;
    durationMinutes: number;
    hoursSet: boolean;
    slots: RdScheduleSlot[];
  };
}

export interface RdScheduleOccurrence {
  startsAt: string;
  localDate: string;
  localTime: string;
  status: RdScheduleOccurrenceStatus;
}

export interface RdSchedulePreviewResponse {
  data: {
    occurrences: RdScheduleOccurrence[];
    visitCount: number;
    firstDate: string;
    lastDate: string;
    /** `monday` ... `sunday` in the RD's zone. */
    weekday: string;
    localTime: string;
    allOpen: boolean;
    /**
     * Always true for a booking: Healthie's create call has no way to hold back
     * its own confirmation email, so "don't email the Client" only governs
     * NDD's emails. The dialog shows `emailNote` beside that choice.
     */
    healthieMayEmail: true;
    emailNote: string;
  };
}

export const RD_SCHEDULE_BOOKING_EMAIL_NOTE =
  "Healthie may still send its own booking confirmation. This choice covers the emails NDD sends.";

export interface RdScheduleAppointmentType {
  id: string;
  name: string;
  durationMinutes: number;
  isInitial: boolean;
  isSCode: boolean;
}

export interface RdScheduleAppointmentTypesResponse {
  data: RdScheduleAppointmentType[];
}

export interface RdScheduleClient {
  patientId: string;
  displayName: string;
  lastVisitAt: string | null;
}

export interface RdScheduleClientsResponse {
  data: RdScheduleClient[];
}

export interface RdScheduleOutcomeOption {
  available: boolean;
  /**
   * Why an outcome is unavailable: a no-show is `before_start`, and "I'm
   * cancelling" is `after_start`.
   */
  reason: "before_start" | "after_start" | null;
}

export interface RdScheduleActionOptionsResponse {
  data: {
    appointment: {
      startsAt: string;
      endsAt: string | null;
      status: string;
      appointmentTypeId: string | null;
      durationMinutes: number | null;
    };
    series: {
      recurringAppointmentId: string;
      weekday: string;
      localTime: string;
      laterVisitCount: number;
      /** More later visits than a change reaches, so "this and all later" is refused. */
      tooLong: boolean;
    } | null;
    outcomes: Record<RdScheduleOutcome, RdScheduleOutcomeOption>;
    suggestedOutcome: RdScheduleOutcome;
    lateCancelWindowHours: number;
    fee: {
      lateCancel: {
        allowed: boolean;
        defaultDecision: RdScheduleFeeDecision;
        reason: string;
        lateCancelsThisQuarter: number;
        amountCents: number | null;
      };
      /** `allowed`: the rule allows this outcome without a fee. */
      noShow: {
        allowed: boolean;
        defaultDecision: RdScheduleFeeDecision;
        reason: string;
        chargeAllowed: boolean;
        amountCents: number | null;
      };
    };
    current: {
      clientNote: string | null;
      /** Healthie's private note as the RD sees it; an edit replaces all of it. */
      privateNote: string | null;
      /** False when Healthie could not be read; the dialog does not offer the note then. */
      privateNoteAvailable: boolean;
      /**
       * `replaces_later_visits` when the visit belongs to a series: an edit with
       * scope "this and all later" writes the same note over every later visit's
       * own note, unchecked. The Edit note dialog's series hint says so from
       * `series`, so the client does not read this field. Null for a single visit.
       */
      privateNoteScopeNote: "replaces_later_visits" | null;
    };
    actionable: {
      reschedule: boolean;
      edit: boolean;
      cancel: boolean;
      delete: boolean;
    };
  };
}

export interface RdScheduleBookAction {
  kind: "book";
  patientId: string;
  appointmentTypeId: string;
  startsAt: string;
  outsideHours: boolean;
  repeat?: { interval: RdScheduleRepeatInterval; count: number } | null;
  privateNote?: string | null;
  clientNote?: string | null;
  emailClient: boolean;
}

export interface RdScheduleRescheduleAction {
  kind: "reschedule";
  appointmentId: string;
  startsAt: string;
  outsideHours: boolean;
  scope: RdScheduleActionScope;
  clientNote?: string | null;
  emailClient: boolean;
}

export interface RdScheduleEditAction {
  kind: "edit";
  appointmentId: string;
  appointmentTypeId?: string | null;
  /**
   * The whole private note Healthie keeps after the edit, not an addition: send
   * the note from the options read with the change applied. An empty string
   * clears it. Omit it (or send null) to leave the note alone.
   */
  privateNote?: string | null;
  /**
   * The private note the RD saw when they began (null for none). Required with
   * `privateNote`; a note changed in Healthie since is refused as changed.
   */
  currentPrivateNote?: string | null;
  clientNote?: string | null;
  scope: RdScheduleActionScope;
  emailClient: boolean;
}

export interface RdScheduleCancelAction {
  kind: "cancel";
  appointmentId: string;
  outcome: RdScheduleOutcome;
  scope: RdScheduleActionScope;
  feeDecision?: RdScheduleFeeDecision | null;
  clientNote?: string | null;
  emailClient: boolean;
}

export interface RdScheduleDeleteAction {
  kind: "delete";
  appointmentId: string;
}

export type RdScheduleActionRequestBody =
  | RdScheduleBookAction
  | RdScheduleRescheduleAction
  | RdScheduleEditAction
  | RdScheduleCancelAction
  | RdScheduleDeleteAction;

/**
 * NO-917: where an NDD-owned visit's copy in Healthie is. `pending` shows a
 * quiet "not in Healthie yet" mark; `failed` is operator-visible and offers
 * the retry. Null on a visit NDD does not own.
 */
export const RD_SCHEDULE_MIRROR_STATES = ["pending", "mirrored", "failed"] as const;
export type RdScheduleMirrorState = (typeof RD_SCHEDULE_MIRROR_STATES)[number];

export interface RdScheduleActionAcceptedResponse {
  /**
   * `saved: true` means NDD already holds the change (core_data.appointments
   * was written in the request): the dialog closes at once and the visit
   * shows. `saved: false` is today's flow (flag off, migration not applied).
   * `appointmentIds` are the NDD appointment ids the save wrote.
   */
  data: {
    actionId: string;
    status: "pending";
    saved?: boolean;
    appointmentIds?: string[];
  };
}

export const RD_SCHEDULE_ACTION_FAILURE_CODES = [
  "slot_taken",
  "appointment_changed",
  "healthie_rejected",
  "healthie_permission_denied",
  "healthie_unavailable",
  "healthie_indeterminate",
  "series_partial",
  "invalid_request",
  "delivery_error",
  "healthie_read_incomplete",
] as const;
export type RdScheduleActionFailureCode =
  (typeof RD_SCHEDULE_ACTION_FAILURE_CODES)[number];

export type RdScheduleActionStatus =
  | "pending"
  | "running"
  | "succeeded"
  | "conflict"
  | "failed";

export interface RdScheduleActionFailure {
  code: RdScheduleActionFailureCode;
  message: string;
  fix: string;
  retryable: boolean;
}

export interface RdScheduleActionStatusResponse {
  data: {
    actionId: string;
    kind: "book" | "reschedule" | "edit" | "cancel" | "delete";
    status: RdScheduleActionStatus;
    healthieAppointmentIds: string[];
    writtenCount: number;
    targetCount: number;
    failure: RdScheduleActionFailure | null;
    actorLane: "own" | "supervised" | "all";
    createdAt: string;
    completedAt: string | null;
    /** NO-917: the mirror state of the visit this action wrote; null when not NDD-owned. */
    mirrorState?: RdScheduleMirrorState | null;
  };
}

/** A synchronous refusal: a closed code, plain copy, and for slot conflicts the dates. */
export interface RdScheduleRefusalResponse {
  error: string;
  code: string;
  dates?: string[];
}

/**
 * What the RD reads when an action does not go through. A closed map, so the
 * dialog never shows Healthie's text, and every entry says how to fix it.
 */
export const RD_SCHEDULE_FAILURE_COPY: Record<
  RdScheduleActionFailureCode,
  { message: string; fix: string; retryable: boolean }
> = {
  slot_taken: {
    message: "Someone booked that time after you opened this.",
    fix: "Pick another open time.",
    retryable: false,
  },
  appointment_changed: {
    message: "This visit changed in Healthie after you opened it.",
    fix: "Close this and open the visit again to see where it stands.",
    retryable: false,
  },
  healthie_rejected: {
    message: "Healthie did not accept the change.",
    fix: "Try again. If it keeps happening, tell an admin.",
    retryable: true,
  },
  healthie_permission_denied: {
    message: "Healthie did not let this account make the change.",
    fix: "Try again. If it keeps happening, ask an admin to check the RD's Healthie access.",
    retryable: true,
  },
  healthie_unavailable: {
    message: "Healthie could not be reached.",
    fix: "Try again in a minute.",
    retryable: true,
  },
  healthie_indeterminate: {
    message: "Healthie did not confirm whether the change went through.",
    fix: "Try again. NDD checks Healthie first, so nothing is done twice.",
    retryable: true,
  },
  series_partial: {
    message: "Only part of the series was changed.",
    fix: "Try again to finish the rest. Visits already changed stay as they are.",
    retryable: true,
  },
  invalid_request: {
    message: "This change could not be sent to Healthie.",
    fix: "Close this and try the change again.",
    retryable: true,
  },
  delivery_error: {
    message: "Something went wrong before this change reached Healthie.",
    fix: "Try again. Nothing was sent to Healthie.",
    retryable: true,
  },
  healthie_read_incomplete: {
    message: "Healthie did not return the whole day, so NDD could not check it.",
    fix: "Try again in a minute. Nothing was sent to Healthie.",
    retryable: true,
  },
};
